import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { PaymentGatewayCustomerDto, PaymentGatewayPaymentProfileDto, SubscriptionGroupMyleDto } from "@/Common/Models/Portal/MedfarPortal";
import { PaymentType } from "@/Common/Models/Portal/MedfarPortalEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { IPortalManagementPopupErrorFunction } from "./LinkMylePatientToPortalAccountDialog";
import { PortalActionConfirmationGenericPopup } from "./PortalActionConfirmationGenericPopup";
import { SubscriptionPaymentInformation_EditPopup } from "./SubscriptionPaymentInformation_EditPopup";

interface ISubscriptionPaymentInformationProps {
    patientContactId: string;
    subscription: SubscriptionGroupMyleDto;
    paymentGatewayCustomer: PaymentGatewayCustomerDto;
}

class SubscriptionPaymentInformationState {
    public showEditDialog: boolean = false;
    public showDeleteConfirmDialog: boolean = false;
    public isModifyingExistingPaymentProfile: boolean;
    public isNewPaymentProfile: boolean;
    public selectedPaymentGatewayPaymentProfileId: string;
}

class SubscriptionPaymentInformation extends React.Component<ISubscriptionPaymentInformationProps, SubscriptionPaymentInformationState> {
    constructor(props: ISubscriptionPaymentInformationProps) {
        super(props);

        const initialState = new SubscriptionPaymentInformationState();
        initialState.showEditDialog = false;

        if (props.paymentGatewayCustomer?.PaymentProfiles?.[0] != null) {
            let primaryIdx = props.paymentGatewayCustomer.PaymentProfiles.indexOfObject("IsDefault", true);

            if (primaryIdx === -1)
                primaryIdx = 0;

            initialState.selectedPaymentGatewayPaymentProfileId = props.paymentGatewayCustomer.PaymentProfiles[primaryIdx].PaymentGatewayPaymentProfileId;
        }

        this.state = initialState;
    }

    public render(): JSX.Element {
        let result: JSX.Element = null;
        let cards: PaymentGatewayPaymentProfileDto[] = [];

        if (this.props.paymentGatewayCustomer != null && this.props.paymentGatewayCustomer.PaymentProfiles != null) {
            this.props.paymentGatewayCustomer.PaymentProfiles.forEach(profile => {
                cards.push(profile);
            });
        }

        let cardSummaries: JSX.Element[] = [];
        cards.forEach((profile, idx) => {
            const className = (profile.PaymentGatewayPaymentProfileId === this.state.selectedPaymentGatewayPaymentProfileId) ? "selected-card" : "";
            cardSummaries.push(
                <div className={className} key={"_payProfile" + idx} onClick={this.selectPaymentProfile.bind(this, profile.PaymentGatewayPaymentProfileId)}>
                    {profile.IsDefault &&
                        <span className="details">✔️</span>
                    }
                    <span className="cardlist-item">{profile.DisplayName}</span>
                    <span className="cardlist-item">{"(" + this.getExpirationDate(profile) + ")"}</span>
                </div>
            );
        });

        let selectedCardProfile: PaymentGatewayPaymentProfileDto = (this.state.selectedPaymentGatewayPaymentProfileId != null && this.state.selectedPaymentGatewayPaymentProfileId)
            ? ArrayHelper.Find(
                cards,
                (c: PaymentGatewayPaymentProfileDto) => c.PaymentGatewayPaymentProfileId === this.state.selectedPaymentGatewayPaymentProfileId
            )
            : null;

        if (cards != null && cards.length > 0 && selectedCardProfile == null) {
            let primaryIdx = cards.indexOfObject("IsDefault", true);
            if (primaryIdx == -1)
                primaryIdx = 0;
            selectedCardProfile = cards[primaryIdx];
        }

        let selectedCardDetails = selectedCardProfile == null
            ? null
            : (
                <div>
                    <div>
                        <div className="details lbl">{cacheStore.GetString("CardNumberLabel")}</div>
                        <div className="details">{selectedCardProfile.CardNumber}</div>
                    </div>
                    <div>
                        <div className="details lbl">{cacheStore.GetString("CardTypeLabel")} </div>
                        <div className="details">{PaymentType[selectedCardProfile.PaymentType]}</div>
                    </div>
                    <div>
                        <div className="details lbl">{cacheStore.GetString("ExpiryLabel")}</div>
                        <div className="details">
                            {this.getExpirationDate(selectedCardProfile)}
                        </div>
                    </div>
                    <div>
                        <div className="details lbl">{cacheStore.GetString("CardholderNameLabel")}</div>
                        <div className="details">{selectedCardProfile.CardholderName}</div>
                    </div>
                    {selectedCardProfile.BillingAddress != null && (
                        <div>
                            <div className="details lbl" style={{ verticalAlign: "top" }}>{cacheStore.GetString("BillingAddressLabel")}</div>
                            <div className="details">
                                <span>{selectedCardProfile.BillingAddress.StreetAddress} {selectedCardProfile.BillingAddress.Company} {selectedCardProfile.BillingAddress.City} </span>
                                <span>{selectedCardProfile.BillingAddress.Province} {selectedCardProfile.BillingAddress.PostCode} {selectedCardProfile.BillingAddress.CountryCode} </span>
                            </div>
                        </div>
                    )}
                    {selectedCardProfile.IsDefault && (
                        <div>
                            <div className="details lbl">{cacheStore.GetString("PrimaryPaymentMethodLabel")}</div>
                            <div className="details">✔️</div>
                        </div>
                    )}
                </div>
            );

        let canModify = !this.state.showEditDialog && this.state.selectedPaymentGatewayPaymentProfileId != null;
        let canAdd = !this.state.showEditDialog;

        result = (
            <div>
                <div className="block separated">
                    {this.state.showEditDialog && (
                        <SubscriptionPaymentInformation_EditPopup
                            patientContactId={this.props.patientContactId}
                            selectedPaymentProfile={this.state.isNewPaymentProfile ? null : selectedCardProfile}
                            onCloseCallback={() => this.onCloseDialog()}
                            onSuccessCallback={response => this.onSaveSuccess(response)}
                        />
                    )}

                    {this.state.showDeleteConfirmDialog && this.state.selectedPaymentGatewayPaymentProfileId && (
                        <PortalActionConfirmationGenericPopup
                            confirmationMessage={`${cacheStore.GetString("DeletePaymentInfoLabel")}: ${selectedCardProfile.DisplayName}`}
                            confirmationTitle={cacheStore.GetString("DeletePaymentInfoTitleLabel")}
                            onCloseCallback={() => this.onCloseRemoveDialog()}
                            confirmationCallback={errorCallback => this.deletePaymentProfile(this.state.selectedPaymentGatewayPaymentProfileId, errorCallback)}
                        />
                    )}

                    <div className="flex-container heading" style={{ marginBottom: "15px" }}>
                        <div>
                            <h3 className="thin-font"> {cacheStore.GetString("SubscriptionLabel")}: {cacheStore.GetString("PaymentInfoLabel")}</h3>
                        </div>
                        <div>
                            <button onClick={this.onShowRemoveDialog.bind(this)} className="btn btn-danger push-right-5" disabled={!canModify}>
                                <span>{cacheStore.GetString("DeleteLabel")}</span>
                            </button>

                            <button onClick={this.displayUpdatePaymentPopup.bind(this)} className="btn btn-primary push-right-5" disabled={!canModify}>
                                <span>{cacheStore.GetString("ModifyLabel")}</span>
                            </button>

                            <button onClick={this.displayNewPaymentPopup.bind(this)} className="btn btn-primary" disabled={!canAdd}>
                                <span>{cacheStore.GetString("AddLabel")}</span>
                            </button>
                        </div>
                    </div>
                    {cardSummaries != null && cardSummaries.length > 0 && (
                        <div className="flex-container center cardlist" style={{ marginBottom: "15px" }}>
                            <div className="cardlist-list">
                                {cardSummaries}
                            </div>
                            {selectedCardDetails != null && (
                                <div className="cardlist-details">
                                    {selectedCardDetails}
                                </div>
                            )}
                        </div>
                    )}
                    {(cardSummaries == null || cardSummaries.length === 0) && (
                        <div className="flex-container center padding-bottom-10 ">
                            {cacheStore.GetString("NoPaymentInfoLabel")}
                        </div>
                    )}
                </div>
            </div>
        );

        return result;
    }

    private selectPaymentProfile(selectedId: string): void {
        const s = new SubscriptionPaymentInformationState();
        s.selectedPaymentGatewayPaymentProfileId = selectedId;
        this.setState(s);
    }

    private displayNewPaymentPopup(): void {
        const s = new SubscriptionPaymentInformationState();
        s.showEditDialog = true;
        s.isModifyingExistingPaymentProfile = false;
        s.isNewPaymentProfile = true;
        this.setState(s);
    }

    private displayUpdatePaymentPopup(): void {
        const s = new SubscriptionPaymentInformationState();
        s.showEditDialog = true;
        s.isModifyingExistingPaymentProfile = true;
        s.isNewPaymentProfile = false;
        this.setState(s);
    }

    private onCloseDialog(): void {
        const s = new SubscriptionPaymentInformationState();
        s.showEditDialog = false;
        this.setState(s);
    }

    private onSaveSuccess(response: ServerEvent): void {
        const errors = response.Errors as ErrorMessage[];

        if (!errors || !errors.length) {
            const op = new LocalEvent(LocalOperations.Portal.PaymentCustomer_Load);
            op.AddItem("patientContactId", this.props.patientContactId);
            LocalEventDispatcher.dispatch(op);
        }

        this.onCloseDialog();
    }

    private getExpirationDate(profile: PaymentGatewayPaymentProfileDto): string {
        return `${profile.ExpirationMonth < 10 ? "0" : ""}${profile.ExpirationMonth} / ${profile.ExpirationYear % 100}`;
    }

    private onShowRemoveDialog(): void {
        const s = new SubscriptionPaymentInformationState();
        s.showDeleteConfirmDialog = true;
        this.setState(s);
    }

    private onCloseRemoveDialog(): void {
        const s = new SubscriptionPaymentInformationState();
        s.showDeleteConfirmDialog = false;
        this.setState(s);
    }

    private deletePaymentProfile(paymentProfileId: string, onError: IPortalManagementPopupErrorFunction): void {
        const requestData = {
            PatientContactId: this.props.patientContactId,
            PaymentGatewayPaymentProfileId: paymentProfileId,
            IsDelete: true,
            IsEdit: false
        };

        new Client().Post(
            ServerOperations.Server.Portal_Proxy_PostPaymentForm,
            requestData,
            response => this.onRemoveSuccess(response, onError),
            ex => onError(ex.toString())
        );
    }

    private onRemoveSuccess(response: ServerEvent, onError: IPortalManagementPopupErrorFunction): void {
        const errors = response.Errors as ErrorMessage[];
        if (errors && errors.length) {
            onError(errors.map(p => p.Message).join(" - "));
            return;
        }

        // todo reload portal patient account section
        const op = new LocalEvent(LocalOperations.Portal.PaymentCustomer_Load);
        op.AddItem("patientContactId", this.props.patientContactId);
        LocalEventDispatcher.dispatch(op);
        this.onCloseRemoveDialog();
    }
}

export { SubscriptionPaymentInformation };
